import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Tooltip } from "@/components/Tooltip";
import { buttonVariants } from "@markeroo/theme";
import type { EditMetricsResponse } from "@/api/client";
import { ui } from "@/strings";
import PiClockDefaultStroke from "@markeroo/icons/src/stroke/time/PiClockDefaultStroke";
import PiFileTextStroke from "@markeroo/icons/src/stroke/files-&-folders/PiFileTextStroke";
import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import PiDrawPencilStroke from "@markeroo/icons/src/stroke/editing/PiDrawPencilStroke";
import PiAlertTriangleStroke from "@markeroo/icons/src/stroke/alerts/PiAlertTriangleStroke";
import PiCheckTickCircleStroke from "@markeroo/icons/src/stroke/general/PiCheckTickCircleStroke";
import PiInformationCircleStroke from "@markeroo/icons/src/stroke/alerts/PiInformationCircleStroke";
import PiCalendarDefaultStroke from "@markeroo/icons/src/stroke/time/PiCalendarDefaultStroke";
import PiPlayCircleStroke from "@markeroo/icons/src/stroke/media/PiPlayCircleStroke";
import {
  generateFindings,
  getVerdictConfig,
  type Finding,
  type FindingIcon,
} from "@markeroo/shared";
import {
  EDIT_METRICS_THRESHOLDS,
  RISK_LEVEL_CONFIG,
  RISK_LABELS,
  type RiskLevel,
} from "./editMetricsConstants";

interface EditMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metrics: EditMetricsResponse;
  riskLevel: RiskLevel;
  /** Optional: Show View Edit Replay button for portal documents */
  replayContext?: {
    classId: string;
    assignmentId: string;
    documentId: string;
  };
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "-";

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
  if (seconds === 0) {
    return "< 1s";
  }
  return `${seconds}s`;
}

function formatNumber(value: number | null, decimals = 1): string {
  if (value === null || !Number.isFinite(value)) return "-";
  return value.toFixed(decimals);
}

function formatPercent(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return "-";
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
}

interface MetricRowProps {
  label: string;
  value: string;
  tooltip?: string;
  warning?: boolean;
}

function MetricRow({ label, value, tooltip, warning }: MetricRowProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-theme-secondary">{label}</span>
        {tooltip && (
          <Tooltip content={tooltip}>
            <PiInformationCircleStroke
              size={16}
              className="text-theme-muted hover:text-theme-secondary"
              aria-hidden="true"
            />
          </Tooltip>
        )}
      </div>
      <span
        className={`text-sm font-medium ${warning ? "text-semantic-warning" : "text-theme-primary"}`}
      >
        {value}
        {warning && (
          <PiAlertTriangleStroke
            size={16}
            className="inline ml-1"
            aria-hidden="true"
          />
        )}
      </span>
    </div>
  );
}

interface MetricSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function MetricSection({ title, icon, children }: MetricSectionProps) {
  return (
    <section className="bg-white rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-theme-muted" aria-hidden="true">
          {icon}
        </span>
        <h3 className="text-sm font-semibold text-theme-primary">{title}</h3>
      </div>
      <div className="space-y-0">{children}</div>
    </section>
  );
}

const RISK_ICONS: Record<
  RiskLevel,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  high: PiAlertTriangleStroke,
  medium: PiAlertTriangleStroke,
  low: PiCheckTickCircleStroke,
  none: PiInformationCircleStroke,
};

type IconComponent = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { size?: number }
>;

const FINDING_ICON_MAP: Record<FindingIcon, IconComponent> = {
  clipboard: PiClipboardCheckStroke,
  clock: PiClockDefaultStroke,
  pencil: PiDrawPencilStroke,
  calendar: PiCalendarDefaultStroke,
  alert: PiAlertTriangleStroke,
};

const VERDICT_COLOR_CLASSES = {
  error: {
    container:
      "rounded-lg border border-semantic-error-border bg-semantic-error-bg p-4",
    text: "text-semantic-error",
    icon: "text-semantic-error",
  },
  warning: {
    container:
      "rounded-lg border border-semantic-warning-border bg-semantic-warning-bg p-4",
    text: "text-semantic-warning-text",
    icon: "text-semantic-warning",
  },
  success: {
    container:
      "rounded-lg border border-semantic-success-border bg-semantic-success-bg p-4",
    text: "text-semantic-success-text",
    icon: "text-semantic-success",
  },
  neutral: {
    container: "rounded-lg border border-border bg-surface-secondary p-4",
    text: "text-theme-muted",
    icon: "text-theme-muted",
  },
};

type ViewMode = "summary" | "details";

function getRiskBadge(level: RiskLevel) {
  const colors = RISK_LEVEL_CONFIG[level];
  const Icon = RISK_ICONS[level];
  const label = RISK_LABELS[level];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${colors.badgeBg} ${colors.badgeText}`}
    >
      <Icon size={16} aria-hidden="true" />
      {label}
    </span>
  );
}

function WarningCallout({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-semantic-warning-bg border border-semantic-warning rounded-lg">
      <PiAlertTriangleStroke
        size={20}
        className="text-semantic-warning flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <p className="text-sm text-semantic-warning-text">{message}</p>
    </div>
  );
}

export function EditMetricsModal({
  isOpen,
  onClose,
  metrics,
  riskLevel,
  replayContext,
}: EditMetricsModalProps) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>("summary");
  const { typing, paste, editBehavior, session } = metrics;
  const T = EDIT_METRICS_THRESHOLDS;

  const handleViewReplay = () => {
    if (replayContext) {
      const { classId, assignmentId, documentId } = replayContext;
      onClose();
      void navigate(`/classes/${classId}/assignments/${assignmentId}/papers/${documentId}/replay`);
    }
  };

  const verdictConfig = getVerdictConfig(riskLevel);
  const VerdictIcon = RISK_ICONS[riskLevel];
  const colorClasses = VERDICT_COLOR_CLASSES[verdictConfig.colorType];

  const findings = useMemo(() => {
    return generateFindings({
      metrics,
      riskLevel,
      wordCount: metrics.finalWordCount,
      uniqueDays: 1,
    });
  }, [metrics, riskLevel]);

  // Warning calculations - include NaN checks for safety
  const highPasteRatio =
    paste.pasteToTypeRatio !== null &&
    Number.isFinite(paste.pasteToTypeRatio) &&
    paste.pasteToTypeRatio > T.PASTE_RATIO_HIGH;
  const lowErrorCorrection =
    editBehavior.errorCorrectionRate !== null &&
    Number.isFinite(editBehavior.errorCorrectionRate) &&
    editBehavior.errorCorrectionRate < T.ERROR_CORRECTION_LOW &&
    editBehavior.insertCount > T.ERROR_CORRECTION_MIN_INSERTS;
  const highWpm =
    typing.wordsPerMinute !== null &&
    Number.isFinite(typing.wordsPerMinute) &&
    typing.wordsPerMinute > T.WPM_HIGH;
  const largePaste =
    Number.isFinite(paste.largestPasteSize) &&
    paste.largestPasteSize > T.PASTE_SIZE_LARGE;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader title={ui.editMetrics.title} onClose={onClose} />
      <ModalBody>
        <div className="space-y-6">
          {/* View Mode Toggle */}
          <div className="flex justify-center">
            <div className="flex gap-1 p-1 bg-surface-secondary rounded-lg">
              <button
                type="button"
                onClick={() => setViewMode("summary")}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === "summary"
                    ? "bg-white text-theme-primary shadow-sm"
                    : "text-theme-secondary hover:text-theme-primary"
                }`}
              >
                {ui.editMetrics.summary}
              </button>
              <button
                type="button"
                onClick={() => setViewMode("details")}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === "details"
                    ? "bg-white text-theme-primary shadow-sm"
                    : "text-theme-secondary hover:text-theme-primary"
                }`}
              >
                {ui.editMetrics.details}
              </button>
            </div>
          </div>

          {viewMode === "summary" ? (
            <>
              {/* Verdict Summary */}
              <div
                className={colorClasses.container}
                role="region"
                aria-label="Writing verdict summary"
              >
                {/* Verdict Badge */}
                <div className="flex items-center gap-2">
                  <VerdictIcon
                    size={20}
                    className={colorClasses.icon}
                    aria-hidden="true"
                  />
                  <span
                    className={`text-sm font-semibold uppercase ${colorClasses.text}`}
                  >
                    {verdictConfig.label}
                  </span>
                </div>

                {/* Detail text */}
                <p className={`mt-1 text-sm ${colorClasses.text}`}>
                  {verdictConfig.detail}
                </p>

                {/* Findings */}
                {findings.length > 0 && (
                  <dl className="mt-4 space-y-3">
                    {findings.map((finding: Finding, index: number) => {
                      const FindingIconComponent =
                        FINDING_ICON_MAP[finding.icon];
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <FindingIconComponent
                            size={16}
                            className={`mt-0.5 flex-shrink-0 ${colorClasses.icon}`}
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <dt
                              className={`text-sm font-medium ${colorClasses.text}`}
                            >
                              {finding.headline}
                            </dt>
                            <dd
                              className={`text-xs ${colorClasses.text} opacity-75`}
                            >
                              {finding.detail}
                            </dd>
                          </div>
                        </div>
                      );
                    })}
                  </dl>
                )}
              </div>

              {/* Summary Stats */}
              <div className="flex items-center justify-between text-xs text-theme-muted px-1">
                <span>
                  {ui.editMetrics.totalEventsAndWords(
                    metrics.totalEvents.toLocaleString(),
                    `${metrics.finalWordCount.toLocaleString()} ${ui.editMetrics.words}`,
                  )}
                </span>
                {metrics.firstEventAt && metrics.lastEventAt && (
                  <span>
                    {/* eslint-disable-next-line no-restricted-syntax -- punctuation between dates */}
                    {formatDate(metrics.firstEventAt)} -{" "}
                    {formatDate(metrics.lastEventAt)}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Risk Summary */}
              <div className="flex items-center justify-between p-4 bg-surface-secondary rounded-lg">
                <div>
                  <p className="text-sm font-medium text-theme-secondary">
                    {ui.editMetrics.overallAssessment}
                  </p>
                  <p className="text-xs text-theme-muted mt-0.5">
                    {ui.editMetrics.riskExplanation}
                  </p>
                </div>
                {getRiskBadge(riskLevel)}
              </div>

              {/* Warning Callouts */}
              {(highPasteRatio ||
                lowErrorCorrection ||
                highWpm ||
                largePaste) && (
                <div className="space-y-2">
                  {highPasteRatio && (
                    <WarningCallout message={ui.editMetrics.highPasteWarning} />
                  )}
                  {lowErrorCorrection && (
                    <WarningCallout
                      message={ui.editMetrics.lowErrorCorrectionWarning}
                    />
                  )}
                  {highWpm && (
                    <WarningCallout message={ui.editMetrics.highWpmWarning} />
                  )}
                  {largePaste && (
                    <WarningCallout
                      message={ui.editMetrics.largePasteWarning}
                    />
                  )}
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Typing Metrics */}
                <MetricSection
                  title={ui.editMetrics.wordsPerMinute}
                  icon={<PiDrawPencilStroke size={20} />}
                >
                  <MetricRow
                    label={ui.editMetrics.wpm}
                    value={formatNumber(typing.wordsPerMinute, 0)}
                    tooltip={ui.editMetrics.wpmTooltip}
                    warning={highWpm}
                  />
                  <MetricRow
                    label={ui.editMetrics.totalKeystrokes}
                    value={typing.totalKeystrokes.toLocaleString()}
                  />
                  <MetricRow
                    label={ui.editMetrics.avgKeystrokeInterval}
                    value={
                      typing.avgKeystrokeIntervalMs !== null
                        ? `${formatNumber(typing.avgKeystrokeIntervalMs, 0)} ${ui.editMetrics.ms}`
                        : "-"
                    }
                  />
                </MetricSection>

                {/* Paste Metrics */}
                <MetricSection
                  title={ui.editMetrics.pastePercentage}
                  icon={<PiClipboardCheckStroke size={20} />}
                >
                  <MetricRow
                    label={ui.editMetrics.pasteRatio}
                    value={formatPercent(paste.pasteToTypeRatio)}
                    tooltip={ui.editMetrics.pasteTooltip}
                    warning={highPasteRatio}
                  />
                  <MetricRow
                    label={ui.editMetrics.pasteCount}
                    value={paste.pasteCount.toString()}
                  />
                  <MetricRow
                    label={ui.editMetrics.largestPaste}
                    value={`${paste.largestPasteSize.toLocaleString()} ${ui.editMetrics.characters}`}
                    warning={largePaste}
                  />
                </MetricSection>

                {/* Session Metrics */}
                <MetricSection
                  title={ui.editMetrics.sessionDuration}
                  icon={<PiClockDefaultStroke size={20} />}
                >
                  <MetricRow
                    label={ui.editMetrics.totalDuration}
                    value={formatDuration(session.totalSessionDurationMs)}
                  />
                  <MetricRow
                    label={ui.editMetrics.activeWritingTime}
                    value={formatDuration(session.activeWritingDurationMs)}
                  />
                  <MetricRow
                    label={ui.editMetrics.sessions}
                    value={session.sessionCount.toString()}
                  />
                </MetricSection>

                {/* Edit Behavior */}
                <MetricSection
                  title={ui.editMetrics.errorCorrectionRate}
                  icon={<PiFileTextStroke size={20} />}
                >
                  <MetricRow
                    label={ui.editMetrics.errorCorrection}
                    value={formatPercent(editBehavior.errorCorrectionRate)}
                    tooltip={ui.editMetrics.errorCorrectionTooltip}
                    warning={lowErrorCorrection}
                  />
                  <MetricRow
                    label={ui.editMetrics.totalEdits}
                    value={editBehavior.totalEdits.toLocaleString()}
                  />
                  <MetricRow
                    label={ui.editMetrics.undoRedo}
                    value={`${editBehavior.undoCount} / ${editBehavior.redoCount}`}
                  />
                </MetricSection>
              </div>

              {/* Summary Stats */}
              <div className="flex items-center justify-between text-xs text-theme-muted px-1">
                <span>
                  {ui.editMetrics.totalEventsAndWords(
                    metrics.totalEvents.toLocaleString(),
                    `${metrics.finalWordCount.toLocaleString()} ${ui.editMetrics.words}`,
                  )}
                </span>
                {metrics.firstEventAt && metrics.lastEventAt && (
                  <span>
                    {/* eslint-disable-next-line no-restricted-syntax -- punctuation between dates */}
                    {formatDate(metrics.firstEventAt)} -{" "}
                    {formatDate(metrics.lastEventAt)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={onClose}
        >
          {ui.actions.close}
        </button>
        {replayContext && metrics.totalEvents > 0 && (
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={handleViewReplay}
          >
            <PiPlayCircleStroke size={16} aria-hidden="true" />
            {ui.editMetrics.viewEditReplay}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
